import { jsPDF } from 'jspdf';
import type { ShopTheme } from '../theme';
import type { LocaleCode } from '../i18n';
import { formatAmountParts, formatLongDate } from '../i18n/format';

/** The printed sheet's wording, lifted from the active locale by `voucherPdfStrings`. */
export interface VoucherPdfStrings {
  giftVoucher: string;
  value: string;
  voucherCode: string;
  quoteCode: string;
  validUntil: string;
  terms: string;
  for: (name: string) => string;
  termsLines: string[];
}

export function voucherPdfStrings(t: (key: string, params?: Record<string, unknown>) => string): VoucherPdfStrings {
  return {
    giftVoucher: t('pdf.gift_voucher'),
    value: t('pdf.value'),
    voucherCode: t('pdf.voucher_code'),
    quoteCode: t('pdf.quote_code'),
    validUntil: t('pdf.valid_until'),
    terms: t('pdf.terms'),
    for: (name: string) => t('pdf.for', { name }),
    termsLines: [t('pdf.terms_line_1'), t('pdf.terms_line_2'), t('pdf.terms_line_3')],
  };
}

export interface VoucherPdfData {
  code: string;
  amount: number;
  currency: string;
  currencySymbol: string;
  venueName: string;
  venueAddress?: string;
  expiryDate?: string;
  message?: string;
  recipientName?: string;
}

type Rgb = [number, number, number];

function hexToRgb(hex: string): Rgb {
  const value = hex.replace('#', '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((c) => c + c)
          .join('')
      : value;
  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
}

/**
 * Loads a remote logo through a canvas so jsPDF gets a data URL it can embed.
 * Returns null when the image is missing or the host refuses a cross-origin read —
 * the voucher then falls back to the venue's name set in type, which is no loss.
 */
async function loadLogoDataUrl(url: string): Promise<{ dataUrl: string; width: number; height: number } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0);
        resolve({ dataUrl: canvas.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

// jsPDF's built-in Helvetica only knows WinAnsi (Latin-1 plus a few typographic marks).
// Anything else — emoji above all — comes out as mojibake, so it is dropped before drawing.
const WIN_ANSI_EXTRAS = '€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ';

export function toWinAnsi(text: string): string {
  const typographic: Record<string, string> = { '\u2009': ' ', '\u202f': ' ', '\u2212': '-', '\u2026': '…' };
  return text
    .normalize('NFC')
    .split('\n')
    .map((line) => {
      const kept = Array.from(line)
        .map((ch) => typographic[ch] ?? ch)
        .filter((ch) => {
          const code = ch.charCodeAt(0);
          return ch.length === 1 && ((code >= 0x20 && code <= 0x7e) || (code >= 0xa0 && code <= 0xff) || WIN_ANSI_EXTRAS.includes(ch));
        })
        .join('')
        .replace(/\s+$/, '');
      // A line that held only emoji disappears instead of leaving a gap
      return line.trim() && !kept.trim() ? null : kept;
    })
    .filter((line): line is string => line !== null)
    .join('\n');
}

const MM_PER_PT = 25.4 / 72;
const RASTER_PX_PER_MM = 12; // ~300 dpi, sharp in print

interface RasterMessage {
  dataUrl: string;
  lineCount: number;
}

/**
 * Sets the personal message through the browser's own text engine, so emoji keep their
 * colours — Helvetica in jsPDF cannot draw them. Wraps to `widthMm` with the same size and
 * leading as the text it replaces. Returns null when there is no canvas to draw on, and the
 * voucher falls back to plain text without the emoji.
 */
function rasterizeMessage(
  message: string,
  widthMm: number,
  fontSizePt: number,
  lineHeightMm: number,
  colour: string
): RasterMessage | null {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const font = `${fontSizePt * MM_PER_PT * RASTER_PX_PER_MM}px Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
    ctx.font = font;
    const maxWidth = widthMm * RASTER_PX_PER_MM;

    const lines: string[] = [];
    for (const paragraph of message.split('\n')) {
      let line = '';
      for (const word of paragraph.split(' ')) {
        const candidate = line ? `${line} ${word}` : word;
        if (line && ctx.measureText(candidate).width > maxWidth) {
          lines.push(line);
          line = word;
        } else {
          line = candidate;
        }
      }
      lines.push(line);
    }

    const lineHeightPx = lineHeightMm * RASTER_PX_PER_MM;
    canvas.width = Math.ceil(maxWidth);
    canvas.height = Math.ceil(lines.length * lineHeightPx);
    // Resizing the canvas resets its state
    ctx.font = font;
    ctx.fillStyle = colour;
    ctx.textBaseline = 'alphabetic';
    // Baselines sit where jsPDF would put them: first one a line height down, less the descent
    const ascent = lineHeightPx * 0.72;
    lines.forEach((line, i) => ctx.fillText(line, 0, ascent + i * lineHeightPx));

    return { dataUrl: canvas.toDataURL('image/png'), lineCount: lines.length };
  } catch {
    return null;
  }
}

/** Draws the perforation as a run of short dashes, matching the card on screen. */
function dashedLine(doc: jsPDF, x: number, yStart: number, yEnd: number, colour: Rgb) {
  doc.setDrawColor(...colour);
  doc.setLineWidth(0.3);
  for (let y = yStart; y < yEnd; y += 3) {
    doc.line(x, y, x, Math.min(y + 1.6, yEnd));
  }
}

/**
 * The voucher as a printable sheet: the same card as the shop, the email and the phone,
 * centred on A4 so it can be handed over folded or slipped into an envelope.
 */
export async function downloadVoucherPdf(
  data: VoucherPdfData,
  theme: ShopTheme,
  locale: LocaleCode,
  strings: VoucherPdfStrings
): Promise<void> {
  const originalMessage = data.message?.trim();
  data = {
    ...data,
    venueName: toWinAnsi(data.venueName),
    venueAddress: data.venueAddress && toWinAnsi(data.venueAddress),
    message: data.message && toWinAnsi(data.message).trim(),
    recipientName: data.recipientName && toWinAnsi(data.recipientName),
  };

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const ink = hexToRgb(theme.ink);
  const body = hexToRgb(theme.body);
  const label = hexToRgb(theme.label);
  const faint = hexToRgb(theme.faint);
  const hairline = hexToRgb(theme.hairline);
  const perforation = hexToRgb(theme.perforation);
  const accent = hexToRgb(theme.accent);
  const paper = hexToRgb(theme.paper);
  const stub = hexToRgb(theme.stub);

  // Paper
  doc.setFillColor(...paper);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  const margin = 20;
  const cardX = margin;
  const cardWidth = pageWidth - margin * 2;
  const cardY = 45;
  const stubWidth = 62;
  const stubX = cardX + cardWidth - stubWidth;

  // The card grows with the personal message so it never spills onto the terms below
  const messageLineHeight = 4.4;
  const messageFontSize = 9;
  const messageWidth = stubX - cardX - 24;
  const messageImage = originalMessage
    ? rasterizeMessage(originalMessage, messageWidth, messageFontSize, messageLineHeight, theme.body)
    : null;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(messageFontSize);
  const messageLines: string[] = messageImage || !data.message ? [] : doc.splitTextToSize(data.message, messageWidth);
  const messageLineCount = messageImage ? messageImage.lineCount : messageLines.length;
  const messageTop = 77; // first baseline, from the card's top edge
  const cardHeight = messageLineCount
    ? Math.max(92, messageTop + (messageLineCount - 1) * messageLineHeight + 10)
    : 92;

  // Card body
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...hairline);
  doc.setLineWidth(0.3);
  doc.rect(cardX, cardY, cardWidth, cardHeight, 'FD');

  // Stub, one shade off white
  doc.setFillColor(...stub);
  doc.rect(stubX, cardY, stubWidth, cardHeight, 'F');
  doc.setDrawColor(...hairline);
  doc.rect(cardX, cardY, cardWidth, cardHeight, 'D');

  // The venue's colour, on the one rule that carries it
  doc.setFillColor(...accent);
  doc.rect(cardX, cardY, cardWidth, 1.1, 'F');

  dashedLine(doc, stubX, cardY + 4, cardY + cardHeight - 4, perforation);

  let y = cardY + 14;

  // Venue logo, or the venue's name set in type
  const logo = theme.logoUrl ? await loadLogoDataUrl(theme.logoUrl) : null;
  if (logo) {
    const maxWidth = 42;
    const maxHeight = 14;
    const ratio = Math.min(maxWidth / logo.width, maxHeight / logo.height);
    const width = logo.width * ratio;
    const height = logo.height * ratio;
    try {
      doc.addImage(logo.dataUrl, 'PNG', cardX + 10, y - 8, width, height, undefined, 'FAST');
      y += Math.max(height - 6, 4);
    } catch {
      /* fall through to the wordmark below */
    }
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...ink);
    doc.text(data.venueName, cardX + 10, y);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...label);
  doc.text(strings.giftVoucher.toUpperCase(), stubX - 10, cardY + 13, { align: 'right' });

  // Value
  y = cardY + 38;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...label);
  doc.text(strings.value.toUpperCase(), cardX + 10, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(16);
  doc.setTextColor(...body);
  doc.text(data.currencySymbol, cardX + 10, y + 16);
  const symbolWidth = doc.getTextWidth(data.currencySymbol);

  const amount = formatAmountParts(data.amount, locale);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(34);
  doc.setTextColor(...ink);
  doc.text(
    amount.cents ? `${amount.whole}${amount.separator}${amount.cents}` : amount.whole,
    cardX + 10 + symbolWidth + 3,
    y + 16
  );

  // Venue line
  doc.setDrawColor(...hairline);
  doc.line(cardX + 10, y + 24, stubX - 10, y + 24);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...ink);
  doc.text(data.venueName, cardX + 10, y + 31);
  if (data.venueAddress) {
    const nameWidth = doc.getTextWidth(data.venueName);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...label);
    doc.text(` · ${data.venueAddress}`, cardX + 10 + nameWidth, y + 31);
  }

  if (messageLineCount) {
    const ruleTop = cardY + messageTop - 4;
    doc.setDrawColor(...hairline);
    doc.line(cardX + 10, ruleTop, cardX + 10, ruleTop + messageLineCount * messageLineHeight);
    if (messageImage) {
      // The image's first line box starts one ascent above the first baseline
      const imageTop = cardY + messageTop - messageLineHeight * 0.72;
      doc.addImage(
        messageImage.dataUrl,
        'PNG',
        cardX + 13,
        imageTop,
        messageWidth,
        messageLineCount * messageLineHeight,
        undefined,
        'FAST'
      );
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(messageFontSize);
      doc.setTextColor(...body);
      doc.text(messageLines, cardX + 13, cardY + messageTop, {
        lineHeightFactor: messageLineHeight / (messageFontSize * MM_PER_PT),
      });
    }
  }

  // Stub — code, expiry, terms
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...label);
  doc.text(strings.voucherCode.toUpperCase(), stubX + 8, cardY + 16);

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...hairline);
  doc.rect(stubX + 8, cardY + 20, stubWidth - 16, 12, 'FD');
  doc.setFont('courier', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...ink);
  doc.text(data.code, stubX + stubWidth / 2, cardY + 28, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...label);
  doc.text(doc.splitTextToSize(strings.quoteCode, stubWidth - 16), stubX + 8, cardY + 39);

  if (data.expiryDate) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...label);
    doc.text(strings.validUntil.toUpperCase(), stubX + 8, cardY + 62);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...ink);
    doc.text(formatLongDate(data.expiryDate, locale), stubX + 8, cardY + 68);
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...faint);
  doc.text(doc.splitTextToSize(strings.terms, stubWidth - 16), stubX + 8, cardY + 76);

  // Page furniture — a line above the card, terms below it
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...label);
  doc.text(strings.giftVoucher.toUpperCase(), margin, 28);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(16);
  doc.setTextColor(...ink);
  doc.text(data.recipientName ? strings.for(data.recipientName) : data.venueName, margin, 36);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...label);
  doc.text(strings.termsLines, margin, cardY + cardHeight + 16);

  doc.setFontSize(8);
  doc.setTextColor(...faint);
  doc.text(data.venueName, margin, pageHeight - 16);

  doc.save(`gift-voucher-${data.code}.pdf`);
}
